import { useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  ControlPolicyStatusToken,
  EmptyState,
  Field,
  PageHeader,
  Select,
  Spinner,
} from '@aigovops/design-system';
import { useOptionalWorkspace } from '../lib/workspace';
import { useWorkspaceQuery } from '../hooks/useWorkspaceQuery';
import { queryKeys } from '../lib/query-keys';
import {
  listControlSpecs,
  listControlSpecOverrides,
  getOrgPolicy,
  type ControlSpecRow as ControlSpec,
  type ControlSpecOverrideRow as ControlSpecOverride,
} from '../lib/api';

const FAMILY_LABELS: Record<string, string> = {
  ogcf: 'Cross-cutting (OGCF)',
  ml_models: 'ML Models',
  llm_services: 'LLM Services',
  ai_agents: 'AI Agents',
  agentic_systems: 'Agentic Systems',
  data_pipelines: 'Data Pipelines',
};

function fetchSpecs(slug: string) {
  return listControlSpecs(slug);
}

function fetchOverrides(slug: string) {
  return listControlSpecOverrides(slug);
}

export function ControlSpecSettings() {
  const workspaceCtx = useOptionalWorkspace();
  const [familyFilter, setFamilyFilter] = useState('all');

  const specs = useWorkspaceQuery(
    (s) => queryKeys.controlSpecs(s),
    (s) => fetchSpecs(s),
  );

  const overrides = useWorkspaceQuery(
    (s) => queryKeys.controlSpecOverrides(s),
    (s) => fetchOverrides(s),
  );

  const orgSlug = workspaceCtx?.workspace.organization_slug ?? null;
  const orgPolicy = useWorkspaceQuery(
    (s) => queryKeys.orgPolicy(orgSlug ?? s),
    () => orgSlug ? getOrgPolicy(orgSlug) : Promise.resolve(null),
  );
  const isFrameworksOnly = orgPolicy.data?.policy_source === 'frameworks_only';

  const activeSpecs = useMemo(
    () => (specs.data?.specs ?? []).filter((s) => s.superseded_at === null),
    [specs.data]
  );

  const filtered = useMemo(
    () => familyFilter === 'all'
      ? activeSpecs
      : activeSpecs.filter((s) => {
          try {
            const a = JSON.parse(s.applicability_json);
            return a.component_class?.includes(familyFilter);
          } catch {
            return familyFilter === 'ogcf';
          }
        }),
    [activeSpecs, familyFilter]
  );

  const overrideMap = useMemo(() => {
    const map = new Map<string, ControlSpecOverride>();
    for (const o of overrides.data?.overrides ?? []) {
      map.set(`${o.control_id}:${o.spec_version}`, o);
    }
    return map;
  }, [overrides.data]);

  if (specs.loading) return <Spinner />;
  if (specs.error) return <Alert variant="error">{specs.error}</Alert>;

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
      <PageHeader
        title="Control specifications"
        subtitle="Machine-evaluable rules that define what 'pass' means for each control. Review, approve, and override thresholds."
      />

      {isFrameworksOnly && (
        <Alert variant="info">
          Your organization uses framework standards only. Jurisdiction-specific
          controls are hidden. Contact your org admin to enable jurisdiction
          requirements.
        </Alert>
      )}

      <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
        <Field id="family-filter" label="Filter by family">
          <Select id="family-filter" value={familyFilter} onChange={(e) => setFamilyFilter(e.target.value)}>
            <option value="all">All families</option>
            {Object.entries(FAMILY_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </Select>
        </Field>
        <span style={{ fontSize: 'var(--font-size-2xs)', color: 'var(--text-dim)' }}>
          {filtered.length} spec{filtered.length !== 1 ? 's' : ''} active
        </span>
      </div>

      {filtered.length === 0 ? (
        <EmptyState
          title="No control specifications"
          description="Control specs are seeded via the editorial queue. Approve pending specs in the review queue to see them here."
        />
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 'var(--space-3)' }}>
          {filtered.map((spec) => {
            const override = overrideMap.get(`${spec.control_id}:${spec.spec_version}`);
            return <SpecCard key={spec.spec_id} spec={spec} override={override ?? null} />;
          })}
        </ul>
      )}
    </div>
  );
}

function SpecCard({ spec, override }: { spec: ControlSpec; override: ControlSpecOverride | null }) {
  const [expanded, setExpanded] = useState(false);

  const threshold = useMemo(() => {
    try { return JSON.parse(spec.threshold_default_json); } catch { return {}; }
  }, [spec.threshold_default_json]);

  const frameworkOverrides = useMemo(() => {
    try { return JSON.parse(spec.framework_overrides_json); } catch { return {}; }
  }, [spec.framework_overrides_json]);

  const sufficiency = useMemo(() => {
    try { return JSON.parse(spec.sufficiency_json); } catch { return {}; }
  }, [spec.sufficiency_json]);

  const signals = useMemo(() => {
    try { return JSON.parse(spec.accepted_signals_json) as Array<Record<string, string>>; } catch { return []; }
  }, [spec.accepted_signals_json]);

  const changelog = useMemo(() => {
    try { return JSON.parse(spec.changelog_json) as string[]; } catch { return []; }
  }, [spec.changelog_json]);

  const freshnessDays = spec.freshness_max_age_seconds != null
    ? Math.round(spec.freshness_max_age_seconds / 86400) : null;

  const thresholdLabel = threshold.metric
    ? `${threshold.metric}: ${threshold.max != null ? `≤ ${threshold.max}` : `≥ ${threshold.min}`}`
    : '—';

  return (
    <li
      style={{
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-card)',
        background: 'var(--surface)',
        padding: 'var(--space-3) var(--space-4)',
      }}
    >
      <div
        style={{
          display: 'flex', justifyContent: 'space-between',
          alignItems: 'center', gap: 'var(--space-2)', cursor: 'pointer',
        }}
        onClick={() => setExpanded((p) => !p)}
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpanded((p) => !p); } }}
      >
        <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
          <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 600, color: 'var(--text)', fontSize: 'var(--font-size-sm)' }}>
            {spec.control_id}
          </span>
          <Badge>v{spec.spec_version}</Badge>
          <ControlPolicyStatusToken status={spec.approved_by ? 'spec_active' : 'spec_pending_review'} />
          {spec.approved_by === 's-nielsen' ? <Badge>baseline</Badge> : spec.approved_by ? <Badge>custom</Badge> : null}
          {override ? <Badge>workspace override</Badge> : null}
        </div>
        <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center', fontSize: 'var(--font-size-2xs)', color: 'var(--text-muted)' }}>
          <span>{thresholdLabel}</span>
          {freshnessDays != null ? <span>{freshnessDays}d freshness</span> : null}
          <span aria-hidden="true">{expanded ? '▾' : '▸'}</span>
        </div>
      </div>

      {expanded ? (
        <div style={{ marginTop: 'var(--space-3)', display: 'grid', gap: 'var(--space-2)', fontSize: 'var(--font-size-2xs)' }}>
          {spec.scope_selector ? (
            <Row label="Scope" value={spec.scope_selector} />
          ) : null}
          <Row label="Baseline threshold" value={thresholdLabel} />
          {Object.entries(frameworkOverrides).map(([fw, t]: [string, any]) => (
            <Row key={fw} label={`Override (${fw})`} value={`${t.metric}: ${t.max != null ? `≤ ${t.max}` : `≥ ${t.min}`}`} />
          ))}
          {override ? (
            <Row label="Workspace override" value={`${JSON.stringify(JSON.parse(override.threshold_json))} — ${override.reason}`} />
          ) : null}
          <Row label="Verdict policy" value={spec.verdict_policy.replace(/_/g, ' ')} />
          <Row label="No data behaviour" value={spec.no_data_behaviour.replace(/_/g, ' ')} />
          {Object.keys(sufficiency).length > 0 ? (
            <Row label="Sufficiency" value={Object.entries(sufficiency).map(([k, v]) => `${k}: ${v}`).join(', ')} />
          ) : null}
          <Row label="Accepted signals" value={signals.map((s) => s.tool ?? s.check_key ?? '?').join(', ')} />
          <Row label="Hash" value={spec.spec_hash} mono />
          {spec.approved_by ? (
            <Row label="Approved" value={`${spec.approved_by} at ${spec.approved_at}`} />
          ) : (
            <Row label="Status" value="Draft — pending editorial approval" />
          )}
          {changelog.length > 0 ? (
            <div style={{ marginTop: 'var(--space-1)' }}>
              <span style={{ color: 'var(--text-dim)' }}>Changelog:</span>
              <ul style={{ margin: '4px 0 0 var(--space-3)', padding: 0 }}>
                {changelog.map((entry, i) => (
                  <li key={i} style={{ color: 'var(--text-muted)' }}>{entry}</li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
      <span style={{ color: 'var(--text-dim)', minWidth: '160px', flexShrink: 0 }}>{label}</span>
      <span style={{ color: 'var(--text)', fontFamily: mono ? 'var(--font-mono)' : undefined, wordBreak: 'break-all' }}>{value}</span>
    </div>
  );
}
